import { useMutation, useQueryClient } from "@tanstack/react-query";
import TenantAPI from "../../api/TenantAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { useAtomValue } from "jotai";

const actionWords = {
  Create: "created",
  Update: "updated",
  Delete: "deleted",
  Duplicate: "duplicated",
};

export type Action = "Create" | "Update" | "Delete" | "Duplicate";

interface Props {
  action: Action;
  actionID?: string;
  payload?: any;
  onSuccess?: () => void;
  onError?: (msg: string) => void;
}

// Create, Update, Delete actions for Bulk Email Actions.
const useBulkEmailCRUD = () => {
  const tenantID = useAtomValue(tenantIDAtom);
  const queryClient = useQueryClient();

  const crudAction = useMutation({
    mutationFn: ({ action, actionID, payload }: Props) => {
      let finalAction = action;

      // Reuse create action for duplicate on the backend.
      // We use duplicate for the "actionsWords" string in the toast.
      if (action === "Duplicate") {
        finalAction = "Create";
      }

      return TenantAPI.crudBulkEmail({
        action: finalAction,
        actionID: actionID,
        payload: payload,
      });
    },
    onSuccess: (_, { onSuccess, action }) => {
      queryClient.invalidateQueries({
        queryKey: ["bulkEmails", tenantID],
      });

      callFunctionIfExists(onSuccess);
      newToastEvent(
        "success",
        `Successfully ${actionWords[action]} email action.`
      );
    },
    onError: (error, { onError }) => {
      const errorMessage = getErrorMessage(
        error,
        "An unknown error occurred. Please try again."
      );

      newToastEvent("error", errorMessage);
      onError && onError(errorMessage);
    },
  });

  return {
    bulkEmailCRUD: crudAction.mutate,
    crudLoading: crudAction.isPending,
  };
};

export default useBulkEmailCRUD;
